import { createRng } from './rng';
import type { Rng } from './rng';
import type { Beat, KitId, RiserShape, SectionName, Verdict } from './types';
import { BEATS_PER_BAR, DEFAULT_BPM } from '../audio/plan';

export type GateShape = 'circle' | 'square' | 'triangle' | 'hex';
export type Curve = RiserShape;

export interface FormPart {
  readonly name: SectionName;
  readonly bar: number;
  readonly bars: number;
}

export const SONG_FORM: readonly FormPart[] = [
  { name: 'intro', bar: 0, bars: 4 },
  { name: 'groove', bar: 4, bars: 8 },
  { name: 'build', bar: 12, bars: 4 },
  { name: 'peak', bar: 16, bars: 8 },
  { name: 'break', bar: 24, bars: 4 },
  { name: 'outro', bar: 28, bars: 4 },
];
export const FORM_BARS = 32;
export const COUNT_IN_BARS = 2;
export const PEAK_BEAT: Beat = 16 * BEATS_PER_BAR;
export const END_BEAT: Beat = FORM_BARS * BEATS_PER_BAR;
export const LAST_APEX_BEAT: Beat = END_BEAT - BEATS_PER_BAR;
export const PRESS_LEAD_BEATS: Beat = 1;
export const SIGHT_BEATS: Beat = 8;
export const SIZE_PER_BEAT = 0.075;
export const FIELD_LIMIT = 0.9;
export const REACH = 0.8;
export const STAB_BEATS: Beat = 1;
export const KEEP_SHARE = 0.5;

export const CALM_PARTS: readonly SectionName[] = ['intro', 'break', 'outro'];
export const CALM_EXTRA_REST: Beat = 2;
const SPOT_TRIES = 12;
const EXP_CURVE = 3.2;
const STUTTER_STEP_BEATS = 0.5;
const STUTTER_TIGHT_STEP_BEATS = 0.25;
const STUTTER_TIGHTEN_AT = 0.75;
const DIP_WIDTH = 0.09;
const DIP_DEPTH = 0.24;
const DIP_MIN_AT = 0.3;
const DIP_MAX_AT = 0.75;
const ROTATION_STEP = Math.PI / 4;
export const KIT_POOL: readonly KitId[] = ['edm', 'phonk', 'funk'];
const EPSILON = 1e-9;

export interface LevelSpec {
  readonly level: number;
  readonly name: string;
  readonly bpm: number;
  readonly holds: readonly Beat[];
  readonly grid: Beat;
  readonly rest: Beat;
  readonly hop: readonly [number, number];
  readonly turn: number;
  readonly slack: number;
  readonly shapes: readonly GateShape[];
  readonly rotate: boolean;
  readonly dipOdds: number;
  readonly stabOdds: number;
  readonly blindOdds: number;
  readonly pairs: boolean;
  readonly curve: Curve;
  readonly still: number;
}

const CIRCLE: LevelSpec = {
  level: 1,
  name: 'Círculo',
  bpm: DEFAULT_BPM,
  holds: [3],
  grid: 4,
  rest: 1,
  hop: [0.18, 0.4],
  turn: Math.PI / 3,
  slack: 1.5,
  shapes: ['circle'],
  rotate: false,
  dipOdds: 0,
  stabOdds: 0,
  blindOdds: 0,
  pairs: false,
  curve: 'linear',
  still: 0,
};
const SIZES: LevelSpec = {
  ...CIRCLE,
  level: 2,
  name: 'Tamanhos',
  holds: [2, 4, 6, 8],
  grid: 2,
  hop: [0.2, 0.5],
  turn: Math.PI / 2,
};
const RUSH: LevelSpec = {
  ...SIZES,
  level: 3,
  name: 'Corrida',
  holds: [2, 3, 4, 6],
  grid: 1,
  hop: [0.3, 0.75],
  turn: (2 * Math.PI) / 3,
};
const SIDES: LevelSpec = {
  ...RUSH,
  level: 4,
  name: 'Lados',
  shapes: ['square', 'triangle', 'hex'],
  rotate: true,
};
const FAKES: LevelSpec = { ...SIDES, level: 5, name: 'Queda falsa', dipOdds: 0.35 };
const STABS: LevelSpec = { ...FAKES, level: 6, name: 'Rajada', stabOdds: 0.3, rest: 1 };
const BLIND: LevelSpec = { ...STABS, level: 7, name: 'Às cegas', blindOdds: 0.25 };
const PAIRS: LevelSpec = { ...BLIND, level: 8, name: 'Dupla', pairs: true };
const EXPERT: LevelSpec = {
  ...PAIRS,
  level: 9,
  name: 'Expert',
  bpm: 140,
  shapes: ['circle', 'square', 'triangle', 'hex'],
  dipOdds: 0.45,
  stabOdds: 0.4,
  blindOdds: 0.35,
};

export const LEVELS: readonly LevelSpec[] = [CIRCLE, SIZES, RUSH, SIDES, FAKES, STABS, BLIND, PAIRS, EXPERT];
export const PLAYABLE_LEVELS = 3;

export interface Note {
  readonly index: number;
  readonly openBeat: Beat;
  readonly apexBeat: Beat;
  readonly holdBeats: Beat;
  readonly size: number;
  readonly x: number;
  readonly y: number;
  readonly shape: GateShape;
  readonly rotation: number;
  readonly dips: readonly number[];
  readonly blind: boolean;
  readonly curve: Curve;
  readonly part: SectionName;
}

export interface Song {
  readonly seed: string;
  readonly level: number;
  readonly bpm: number;
  readonly kit: KitId;
  readonly form: readonly FormPart[];
  readonly notes: readonly Note[];
  readonly endBeat: Beat;
}

export interface Slot {
  readonly hold: Beat;
  readonly apex: Beat;
}

export function levelSpec(level: number): LevelSpec {
  const index = Math.min(LEVELS.length, Math.max(1, Math.floor(level))) - 1;
  return LEVELS[index] ?? CIRCLE;
}

export function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return 1;
  return Math.min(PLAYABLE_LEVELS, Math.max(1, Math.floor(level)));
}

export function partAt(beat: Beat, form: readonly FormPart[] = SONG_FORM): SectionName {
  const bar = Math.floor(beat / BEATS_PER_BAR);
  let name: SectionName = form[0]?.name ?? 'intro';
  for (const part of form) {
    if (part.bar <= bar) name = part.name;
  }
  return name;
}

function ceilToGrid(beat: Beat, grid: Beat): Beat {
  return Math.ceil(beat / grid - EPSILON) * grid;
}

function restAfter(spec: LevelSpec, apex: Beat): Beat {
  return spec.rest + (CALM_PARTS.includes(partAt(apex)) ? CALM_EXTRA_REST : 0);
}

export function anchorHold(spec: LevelSpec): Beat {
  return Math.max(...spec.holds);
}

function place(rng: Rng, spec: LevelSpec, cursor: Beat, limit: Beat): Slot | null {
  const top = anchorHold(spec);
  const pool = spec.holds.length > 1 ? spec.holds.filter((hold) => hold < top) : spec.holds;
  const stab = spec.stabOdds > 0 && rng.chance(spec.stabOdds);
  const wanted = stab ? STAB_BEATS : rng.pick(pool);
  const candidates = [wanted, ...[...pool].sort((a, b) => a - b).filter((hold) => hold < wanted)];
  for (const hold of candidates) {
    const apex = ceilToGrid(cursor + hold, hold === STAB_BEATS && stab ? 1 : spec.grid);
    if (apex <= limit + EPSILON) return { hold, apex };
  }
  return null;
}

function clampField(value: number, size: number): number {
  const bound = Math.max(0, FIELD_LIMIT - size);
  return Math.min(bound, Math.max(-bound, value));
}

interface Step {
  readonly x: number;
  readonly y: number;
  readonly heading: number;
}

const HOME_STEP: Step = { x: 0, y: 0, heading: 0 };

export function isGuided(note: Note, still: number): boolean {
  return note.index < still;
}

function inField(value: number, size: number): boolean {
  return Math.abs(value) <= Math.max(0, FIELD_LIMIT - size) + EPSILON;
}

function spot(rng: Rng, spec: LevelSpec, from: Step, size: number): Step {
  const [near, far] = spec.hop;
  for (let attempt = 0; attempt < SPOT_TRIES; attempt++) {
    const spread = attempt < SPOT_TRIES / 2 ? spec.turn : Math.PI;
    const heading = from.heading + rng.range(-spread, spread);
    const hop = rng.range(near, far);
    const x = from.x + Math.cos(heading) * hop;
    const y = from.y + Math.sin(heading) * hop;
    if (inField(x, size) && inField(y, size)) return { x, y, heading };
  }
  const heading = Math.hypot(from.x, from.y) > EPSILON ? Math.atan2(-from.y, -from.x) : from.heading;
  const x = clampField(from.x + Math.cos(heading) * near, size);
  const y = clampField(from.y + Math.sin(heading) * near, size);
  return { x, y, heading };
}

export function createSong(seed: string, level: number): Song {
  const spec = levelSpec(level);
  const kit = createRng(seed).fork('kit').pick(KIT_POOL);
  const rng = createRng(seed).fork(`n${String(spec.level)}`);
  const slots: Slot[] = [];
  const topHold = anchorHold(spec);
  const anchorOpen = PEAK_BEAT - topHold;

  let cursor: Beat = COUNT_IN_BARS * BEATS_PER_BAR;
  for (;;) {
    const slot = place(rng, spec, cursor, anchorOpen - spec.rest);
    if (slot === null) break;
    slots.push(slot);
    cursor = slot.apex + restAfter(spec, slot.apex);
  }
  slots.push({ hold: topHold, apex: PEAK_BEAT });
  cursor = PEAK_BEAT + restAfter(spec, PEAK_BEAT);
  for (;;) {
    const slot = place(rng, spec, cursor, LAST_APEX_BEAT);
    if (slot === null) break;
    slots.push(slot);
    cursor = slot.apex + restAfter(spec, slot.apex);
  }

  return { seed, level: spec.level, bpm: spec.bpm, kit, form: SONG_FORM, notes: dressNotes(rng, spec, slots), endBeat: END_BEAT };
}

export function dressNotes(rng: Rng, spec: LevelSpec, slots: readonly Slot[], partOf: (beat: Beat) => SectionName = partAt): Note[] {
  let from: Step = { x: 0, y: 0, heading: rng.range(0, Math.PI * 2) };
  return slots.map((slot, index): Note => {
    const size = SIZE_PER_BEAT * slot.hold;
    const at = index < spec.still ? { ...HOME_STEP, heading: from.heading } : spot(rng, spec, from, size);
    from = at;
    const shape = rng.pick(spec.shapes);
    const rotation = spec.rotate && shape !== 'circle' ? rng.int(0, 8) * ROTATION_STEP : 0;
    const dips = spec.dipOdds > 0 && slot.hold > STAB_BEATS && rng.chance(spec.dipOdds)
      ? [rng.range(DIP_MIN_AT, DIP_MAX_AT)]
      : [];
    const blind = spec.blindOdds > 0 && rng.chance(spec.blindOdds);
    return {
      index,
      openBeat: slot.apex - slot.hold,
      apexBeat: slot.apex,
      holdBeats: slot.hold,
      size,
      x: at.x,
      y: at.y,
      shape,
      rotation,
      dips,
      blind,
      curve: spec.curve,
      part: partOf(slot.apex),
    };
  });
}

function expCurve(p: number): number {
  return (Math.exp(EXP_CURVE * p) - 1) / (Math.exp(EXP_CURVE) - 1);
}

function stutterCurve(p: number, holdBeats: Beat): number {
  const step = p >= STUTTER_TIGHTEN_AT ? STUTTER_TIGHT_STEP_BEATS : STUTTER_STEP_BEATS;
  const quantized = Math.floor((p * holdBeats) / step) * step;
  return expCurve(Math.min(1, Math.max(0, quantized / holdBeats)));
}

export function overshootSlope(curve: Curve): number {
  if (curve === 'linear') return 1;
  return (EXP_CURVE * Math.exp(EXP_CURVE)) / (Math.exp(EXP_CURVE) - 1);
}

export function dipDepthAt(note: Note, p: number): number {
  let depth = 0;
  for (const dip of note.dips) {
    const offset = p - dip;
    if (offset < 0 || offset > DIP_WIDTH) continue;
    depth = Math.max(depth, Math.sin(Math.PI * (offset / DIP_WIDTH)) * DIP_DEPTH);
  }
  return depth;
}

export function progressAt(note: Note, beat: Beat): number {
  const span = note.apexBeat - note.openBeat;
  if (span <= 0) return beat >= note.apexBeat ? 1 : 0;
  return (beat - note.openBeat) / span;
}

export function growthAt(note: Note, beat: Beat): number {
  const p = progressAt(note, beat);
  if (p <= 0) return 0;
  if (p >= 1) return 1 + overshootSlope(note.curve) * (p - 1);
  const base = note.curve === 'linear' ? p : note.curve === 'exp' ? expCurve(p) : stutterCurve(p, note.holdBeats);
  return Math.max(0, base - dipDepthAt(note, p));
}

export function sizeAt(note: Note, beat: Beat): number {
  return note.size * growthAt(note, beat);
}

export function pressOpen(note: Note): Beat {
  return note.openBeat - PRESS_LEAD_BEATS;
}

export function approachBeats(note: Note): Beat {
  return note.holdBeats + PRESS_LEAD_BEATS + SIGHT_BEATS;
}

export function pressableNote(
  notes: readonly Note[],
  beat: Beat,
  done: (index: number) => boolean,
): Note | undefined {
  for (const note of notes) {
    if (done(note.index)) continue;
    if (beat >= pressOpen(note) - EPSILON && beat <= note.apexBeat + EPSILON) return note;
  }
  return undefined;
}

const STEP_DOWN: Record<Verdict, Verdict> = {
  perfect: 'good',
  good: 'weak',
  weak: 'flop',
  flop: 'flop',
  miss: 'miss',
};

export function gradePosition(verdict: Verdict, distance: number, radius: number, slack = 1): Verdict {
  if (verdict === 'miss') return 'miss';
  const reach = radius * slack;
  if (distance <= KEEP_SHARE * reach + EPSILON) return verdict;
  if (distance <= reach + EPSILON) return STEP_DOWN[verdict];
  return 'flop';
}
